import axios from "axios";
import Swal from "sweetalert2";
import { useState } from "react";
import { Helmet } from "react-helmet";
import "./contact.css";

const CONTACT_ITEMS = [
  {
    icon: "fa-location-dot",
    label: "Address",
    content: (
      <a
        href="https://maps.app.goo.gl/zrcxbYQYP8Aw8Dbs7"
        target="_blank"
        rel="noopener noreferrer"
      >
        1018, Anand Mangal 3, Near Apollo Hospital, Ambawadi, Ahmedabad, Gujarat
        - 380054, India
      </a>
    ),
  },
  {
    icon: "fa-phone",
    label: "Phone",
    content: <a href="tel:+918460028397">+91 84600 28397</a>,
  },
  {
    icon: "fa-envelope",
    label: "Email",
    content: <a href="mailto:info@gpsfinvest.com">info@gpsfinvest.com</a>,
  },
  {
    icon: "fa-clock",
    label: "Working Hours",
    content: "Mon - Sat 10:00 AM - 6:00 PM",
  },
];

const CONTACT_FEATURES = [
  {
    icon: "fa-award",
    title: "Expert Guidance",
    text: "Get advice from financial experts",
  },
  {
    icon: "fa-bolt",
    title: "Quick Response",
    text: "We respond within 24 business hours",
  },
  {
    icon: "fa-handshake",
    title: "Trusted Partner",
    text: "Transparent, reliable & client-focused",
  },
  {
    icon: "fa-shield-halved",
    title: "Secure & Confidential",
    text: "Your information is always protected",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+91",
    inquiryType: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email))
      newErrors.email = "Please enter a valid email address";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!validatePhone(formData.phone))
      newErrors.phone = "Please enter a valid 10-digit Indian mobile number";
    if (!formData.inquiryType)
      newErrors.inquiryType = "Please select an inquiry type";
    if (!formData.message.trim())
      newErrors.message = "Please enter your message";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire("Warning", "Please fix the highlighted errors", "warning");
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/contact/send`,
        formData
      );

      if (res.data.status === 1) {
        Swal.fire("Success", "Your message has been sent!", "success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          countryCode: "+91",
          inquiryType: "",
          message: "",
        });
      } else {
        Swal.fire("Error", res.data.msg, "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong. Try again later.", "error");
    }
  };

  return (
    <>
      <Helmet>
        <title>GPS Finvest - Contact Us</title>
        <meta
          name="description"
          content="Get in touch with GPS Finvest for financial consultations, loan inquiries, mutual fund advice, or any other services. Contact us today!"
        />
      </Helmet>

      <section className="contact-hero">
        <div className="contact-hero-inner">
          <div className="contact-info">
            <p className="contact-eyebrow">GET IN TOUCH</p>
            <h1 className="contact-title">
              We&apos;re Here to Help You <span>Grow</span>
            </h1>
            <p className="contact-lead">
              Have a question, need expert advice, or want to explore our
              services? Reach out to us and our team will get back to you
              promptly.
            </p>

            <ul className="contact-list">
              {CONTACT_ITEMS.map((item) => (
                <li key={item.label}>
                  <span className="contact-list-icon" aria-hidden="true">
                    <i className={`fa-solid ${item.icon}`} />
                  </span>
                  <div>
                    <strong>{item.label}</strong>
                    <div className="contact-list-value">{item.content}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="contact-form-card">
            <div className="contact-form-head">
              <div className="contact-form-head-icon" aria-hidden="true">
                <i className="fa-solid fa-comments" />
              </div>
              <div>
                <h2>Send Us a Message</h2>
                <p>Fill out the form and we&apos;ll get back to you shortly.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="contact-form-grid">
                <div className="contact-field">
                  <label htmlFor="contact-name">Full Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    className={errors.name ? "error-input" : ""}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                  />
                  {errors.name && <p className="error-text">{errors.name}</p>}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-email">Email Address</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    className={errors.email ? "error-input" : ""}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="error-text">{errors.email}</p>}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-phone">Phone Number</label>
                  <div className="contact-phone-group">
                    <select
                      name="countryCode"
                      value={formData.countryCode || "+91"}
                      onChange={handleChange}
                      aria-label="Country code"
                    >
                      <option value="+91">IN +91</option>
                      <option value="+1">US +1</option>
                      <option value="+44">UK +44</option>
                      <option value="+971">AE +971</option>
                      <option value="+61">AU +61</option>
                      <option value="+65">SG +65</option>
                    </select>
                    <input
                      id="contact-phone"
                      type="tel"
                      inputMode="numeric"
                      name="phone"
                      className={errors.phone ? "error-input" : ""}
                      value={formData.phone || ""}
                      placeholder="Enter Mobile No."
                      onChange={(e) => {
                        const cleaned = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        handleChange({
                          target: { name: "phone", value: cleaned },
                        });
                      }}
                    />
                  </div>
                  {errors.phone && <p className="error-text">{errors.phone}</p>}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-inquiry">Inquiry Type</label>
                  <select
                    id="contact-inquiry"
                    name="inquiryType"
                    className={errors.inquiryType ? "error-input" : ""}
                    value={formData.inquiryType}
                    onChange={handleChange}
                  >
                    <option value="">Select Inquiry Type</option>
                    <option value="Investor">Investor</option>
                    <option value="Mutual Fund Advisor">
                      Mutual Fund Advisor
                    </option>
                    <option value="Sales">Sales Inquiry</option>
                    <option value="General">General Inquiry</option>
                  </select>
                  {errors.inquiryType && (
                    <p className="error-text">{errors.inquiryType}</p>
                  )}
                </div>

                <div className="contact-field contact-field-full">
                  <label htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows="5"
                    className={errors.message ? "error-input" : ""}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us what we can help you with!"
                  />
                  {errors.message && (
                    <p className="error-text">{errors.message}</p>
                  )}
                </div>
              </div>

              <button type="submit" className="contact-submit">
                <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                Send Message
              </button>

              <p className="contact-privacy">
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                We respect your privacy. Your information is safe with us.
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="contact-secondary">
        <div className="contact-secondary-inner">
          <div className="contact-features">
            {CONTACT_FEATURES.map((item) => (
              <div key={item.title} className="contact-feature">
                <span className="contact-feature-icon" aria-hidden="true">
                  <i className={`fa-solid ${item.icon}`} />
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="contact-find">
            <h2 className="contact-find-title">Find Us</h2>
            <div className="contact-map-frame">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3495.242618239306!2d72.55251597531364!3d23.01802887917692!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e84e325c26985%3A0x47da16a1b753a025!2sAnand%20Mangal%203!5e1!3m2!1sen!2sin!4v1763534264269!5m2!1sen!2sin"
                width="100%"
                height="420"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Anand Mangal 3 Map"
              />
            </div>
          </div>

          <div className="contact-cta">
            <div className="contact-cta-art" aria-hidden="true">
              <div className="contact-cta-ring">
                <i className="fa-solid fa-envelope-open-text" />
                <span className="contact-cta-plane">
                  <i className="fa-solid fa-paper-plane" />
                </span>
              </div>
            </div>
            <div className="contact-cta-copy">
              <h2>Let&apos;s Start a Conversation</h2>
              <p>
                We&apos;re excited to learn about your goals and explore how we
                can help you achieve them.
              </p>
            </div>
            <a href="tel:+918460028397" className="contact-cta-btn">
              Talk to an Expert
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
